import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RegInviteModalStates, RegistrationInviteModalProps } from '../InviteResident';
import { clearInvitationResult, selectAuthState, sendInvitation } from '../../../shared/sagas/auth/authSlice';
import { RegistrationTokenRequestDto, Role } from '@inosio/payments-sdk';

export interface SubjectDetailsInvitation {
  ProfileUuid: string;
  Email: string;
  FirstName: string;
  MiddleName?: string;
  LastName: string;
  RoleType: Role;
  TokenId?: string;
  UpdateTokenId?: string
}

//The options on the invite user modal
export enum InvitationOptions {
  SendActivationLink = 'SendActivationLink',
  GetActivationLink = 'GetActivationLink',
  GetShortCode = 'GetShortCode',
}

/**
 * This custom hook serves as an object factory (sort of) that packages the dependencies of the regInviteModal
 * into one function so that the modal can easily be reused accross the application without duplicating code.
 * @param props all self explanatory but particular attention should be paid to handleCloseModal,
 * this is used to clean up the states and may mean different things depending on the context... for e.g in the use case for the resident, we carry out 2 steps:
 * 1. reset the currentSelection i.e setSelectedResident({})
 * 2. turn off the control flags for the modal
 * @returns all the parameters needed to use the regInviteModal for registration invites.
 */
export default function useRegInviteConfigs(props: { handleCloseModal: () => void; openModal: boolean; subjectDetails: SubjectDetailsInvitation }): RegistrationInviteModalProps {
  const dispatch = useDispatch();
  const { invitationDetails, sendingInvitationError, isSendingInvitationFailed, isSendingInvitationSuccess } = useSelector(selectAuthState);

  const [regInviteModalState, setRegInviteModalState] = React.useState(RegInviteModalStates.InviteToPortal);
  const [selectedInvitationOption, setSelectedInvitationOption] = React.useState(InvitationOptions.SendActivationLink);
  const [activationLink, setActivationLink] = React.useState('');
  const [specialCode, setSpecialCode] = React.useState('');
  const [invitationError, setInvitationError] = React.useState('');
  const [isSpinnerModalOpen, setIsSpinnerModalOpen] = React.useState(false);
  const [spinnerStatus, setSpinnerStatus] = React.useState<string | null>(null);

  const handleCloseRegInviteModal = () => {
    props.handleCloseModal();
    setRegInviteModalState(RegInviteModalStates.InviteToPortal);
    setSelectedInvitationOption(InvitationOptions.SendActivationLink);
    setActivationLink('');
    setSpecialCode('');
    dispatch({ type: clearInvitationResult.type });
  };

  const handleGetInvitation = (invitationType: InvitationOptions, uuid: string, email: string, firstName: string, lastName: string, middleName?: string,updateTokenId?: string) => {
    setSelectedInvitationOption(invitationType);
    setIsSpinnerModalOpen(true);
    dispatch({
      type: sendInvitation.type,
      payload: {
        sendEmail: invitationType === InvitationOptions.SendActivationLink,
        registrationTokenRequestDto: {
          Email: email,
          EntityUuid: uuid,
          FirstName: firstName,
          MiddleName: middleName?.trim() || null,
          LastName: lastName,
          UpdateTokenId: updateTokenId
        } as RegistrationTokenRequestDto,
      },
    });
  };

  const resetInviteResults = () => {
    dispatch({ type: clearInvitationResult.type });
  };

  useEffect(() => {
    if (isSendingInvitationFailed) {
      setIsSpinnerModalOpen(false);
      setInvitationError(sendingInvitationError);
    }
  }, [isSendingInvitationFailed]);


  useEffect(() => {
    if (isSendingInvitationSuccess) {
      if (regInviteModalState === RegInviteModalStates.InvitationEmail && selectedInvitationOption === InvitationOptions.SendActivationLink) {
        setActivationLink(invitationDetails.RegistrationLink);
        setRegInviteModalState(RegInviteModalStates.ActivationLink);
      } else if (selectedInvitationOption === InvitationOptions.GetShortCode) {
        setSpecialCode(invitationDetails.ShortCode);
        setRegInviteModalState(RegInviteModalStates.SpecialCode);
      } else if (selectedInvitationOption === InvitationOptions.GetActivationLink) {
        setActivationLink(invitationDetails.RegistrationLink);
        setRegInviteModalState(RegInviteModalStates.InvitationLink);
      }
      setSpinnerStatus('stop');
    }
  }, [isSendingInvitationSuccess]);

  return {
    handleClose: handleCloseRegInviteModal,
    open: props.openModal,
    subjectDetails: props.subjectDetails,
    setIsRISpinnerModalOpen: setIsSpinnerModalOpen,
    isRISpinnerModalOpen: isSpinnerModalOpen,
    invitationError,
    setInvitationError,
    activationLink,
    specialCode,
    handleGetInvitation,
    resetInviteResults,
    rISpinnerStatus: spinnerStatus,
    modalState: regInviteModalState,
    setModalState: setRegInviteModalState,
  };
}
